import type { ScheduleTime, TariffGroup } from '@src/core/buyerApiClient';
import {
  collectTariffPrices,
  getSelectedTariff,
  getTariffPriceForScheduleTime,
  type ITariffPrice,
} from '@src/domains/tariffs';
import { schedulingContainsMinute } from '@src/common/schedulings';
import type { TariffsPerType } from '@src/stores/product';

// Price the card advertises while nothing is selected, resolved from the app's
// featured-tariff setting by `useUnitDisplayPrice`.
export type PreviewPrice = { cents: number; oldCents: number | null };

export type UnitPrice =
  | { kind: 'single'; cents: number; oldCents: number | null }
  | { kind: 'range'; minCents: number; maxCents: number };

type ComputeUnitPriceInput = {
  defaultTariffGroup: TariffGroup | undefined;
  listOfTariffsPerType: TariffsPerType[];
  selectedHour: ScheduleTime | null;
  // Min/max price across every bookable tariff for the current month. Used as
  // the "starting from" floor before a date is picked, so selecting a date
  // never swaps the displayed price below the advertised value (within the
  // current month). Computed by `useUnitDisplayPrice` via
  // `getSchedulingsTariffPreview` over the current-month schedulings.
  monthPriceRange?: { minCents: number; maxCents: number } | null;
  // Set only while nothing is selected and the configured mode resolves a price.
  // Null everywhere else, which leaves every path below untouched.
  previewPrice?: PreviewPrice | null;
};

// Extends the canonical v1 walking (`collectTariffPrices`) by pre-filtering
// the TariffsPerType's schedulings to those containing the selected hour and
// restricting each scheduling's per-hour price entries to that hour. With no
// `selectedHour`, delegates straight to the base helper.
function collectTariffPricesNarrowedByHour(
  tariffPerType: TariffsPerType,
  selectedHour: ScheduleTime | null,
): ITariffPrice[] {
  if (!selectedHour) return collectTariffPrices(tariffPerType);

  const narrowed: TariffsPerType = {
    ...tariffPerType,
    schedulings: tariffPerType.schedulings
      .filter((sch) => schedulingContainsMinute(sch, selectedHour.minutes))
      .map((sch) => ({
        ...sch,
        tariffScheduleUnitPrices: sch.tariffScheduleUnitPrices.filter(
          (tsup) => tsup.time === selectedHour.formattedTime,
        ),
      })),
  };

  return collectTariffPrices(narrowed);
}

function buildPriceFromCents(prices: number[], oldCents: number | null = null): UnitPrice | null {
  if (prices.length === 0) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return { kind: 'single', cents: min, oldCents };
  return { kind: 'range', minCents: min, maxCents: max };
}

export function computeUnitPrice(input: ComputeUnitPriceInput): UnitPrice | null {
  const { defaultTariffGroup, listOfTariffsPerType, selectedHour, monthPriceRange, previewPrice } = input;

  const selectedTariffsPerType = listOfTariffsPerType.filter((t) => t.selectedQuantity > 0);
  const hasSelection = selectedTariffsPerType.length > 0;

  // With nothing picked, the card advertises the tariff the seller configured
  // rather than the cheapest one. Any selection hands control back to the walks
  // below, which must price what was actually chosen.
  if (!hasSelection && !selectedHour && previewPrice) {
    return { kind: 'single', ...previewPrice };
  }

  const selectedTariffPerType = selectedTariffsPerType[0];
  if (selectedTariffPerType && selectedHour) {
    const tariffPrice = getTariffPriceForScheduleTime(selectedTariffPerType, selectedHour);
    if (tariffPrice) {
      return { kind: 'single', cents: tariffPrice.priceCents, oldCents: tariffPrice.oldPriceCents ?? null };
    }
  }

  // Once the user has chosen quantities, the "starting from" range must reflect
  // only the selected tariff types — otherwise an unselected (cheaper) tariff
  // would leak into the displayed unit price after a selection.
  // Mirror v1's range walk: oldPriceCents is only carried over when there's
  // exactly one collected entry (productAdder/core/tariffPrice.ts:44-46).
  // With N entries, v1 drops it — even if min === max — so the discount
  // badge / struck-through price only renders for the unambiguous single
  // case.
  const isDependentType = (t: TariffsPerType): boolean => {
    const tariff = getSelectedTariff(t, selectedHour ? selectedHour.minutes : null);
    return Boolean(tariff?.isDependent);
  };
  const sourceList = hasSelection ? selectedTariffsPerType : listOfTariffsPerType.filter((t) => !isDependentType(t));
  const narrowed = sourceList.flatMap((t) => collectTariffPricesNarrowedByHour(t, selectedHour));
  if (narrowed.length > 0) {
    const prices = narrowed.map((p) => p.priceCents);
    const oldCents = narrowed.length === 1 ? narrowed[0]!.oldPriceCents : null;
    return buildPriceFromCents(prices, oldCents);
  }

  if (monthPriceRange) {
    if (monthPriceRange.minCents === monthPriceRange.maxCents) {
      return { kind: 'single', cents: monthPriceRange.minCents, oldCents: null };
    }
    return { kind: 'range', minCents: monthPriceRange.minCents, maxCents: monthPriceRange.maxCents };
  }

  const candidates = (defaultTariffGroup?.tariffs ?? []).filter((t) => !t.isDependent);
  const fallbackPrices = candidates.map((t) => t.priceCents).filter((c): c is number => typeof c === 'number');
  const fallbackOldCents = fallbackPrices.length === 1 ? candidates[0]?.oldPriceCents ?? null : null;
  return buildPriceFromCents(fallbackPrices, fallbackOldCents);
}
